import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); setMessage("This verification link is missing its token."); return; }
    api.post("/auth/verify-link", { token })
      .then(({ data }) => {
        applySession(data);
        setState("success");
        setTimeout(() => navigate("/dashboard"), 1800);
      })
      .catch((e) => { setState("error"); setMessage(formatApiError(e.response?.data?.detail)); });
  }, [params, applySession, navigate]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 gradient-hero">
      <div className="glass neon-border rounded-2xl p-10 max-w-md w-full text-center" data-testid="verify-email-page">
        <div className="flex justify-center mb-6"><Logo size={44} tagline /></div>
        {state === "verifying" && (
          <>
            <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white">Verifying your email…</h1>
            <p className="text-slate-400 text-sm mt-2">Hang tight, this only takes a second.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" data-testid="verify-success-icon" />
            <h1 className="font-heading text-2xl font-bold text-white">Email verified!</h1>
            <p className="text-slate-400 text-sm mt-2">Your account is unlocked. Taking you to your dashboard…</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-[#FF5E7E] mx-auto mb-4" data-testid="verify-error-icon" />
            <h1 className="font-heading text-2xl font-bold text-white">Verification failed</h1>
            <p className="text-slate-400 text-sm mt-2">{message}</p>
            <Link to="/dashboard" className="inline-block mt-6 btn-neon rounded-full px-6 py-2.5 text-sm font-semibold">Go to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
